import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  productsUrl, productByIdUrl, categoriesUrl,
  uploadProductImageUrl, nextSkuUrl, ASSETS,
} from '../../BaseURL';
import Modal from '../../components/Modal';
import BarcodeScannerModal from '../../components/BarcodeScannerModal';
import BarcodeLabel from '../../components/BarcodeLabel';
import { fmtMoney, fmtNumber } from '../../utils/format';
import { toastSuccess, swalDangerConfirm, showAxiosError } from '../../utils/swal';
import { defaultImageFor } from '../../utils/productImage';
import { generateEAN13, renderBarcode } from '../../utils/barcode';
import { useFacility } from '../../context/FacilityContext';

const emptyProduct = {
  name: '', sku: '', barcode: '', category_id: '',
  brand: '', unit: 'pcs', cost_price: '', selling_price: '',
  use_manual_price: false,
  tax_percent: 0, reorder_level: 0, image: null, description: '',
  initial_stock: '',
};

// Round to nearest currency cent so the input doesn't get long float trails.
const round2 = (n) => Math.round(Number(n) * 100) / 100;

// Expanded unit list — includes pharmacy forms (blister, strip, vial,
// ampoule, drops, tube, jar) so the default-image picker can route correctly.
const UNITS = [
  'pcs', 'pack', 'box', 'carton', 'kg', 'g', 'L', 'ml', 'm', 'dozen',
  'bottle', 'tablet', 'capsule', 'blister', 'strip', 'sachet',
  'vial', 'ampoule', 'tube', 'jar', 'drops', 'roll', 'bar', 'serving',
];

// Resolve a product to its display image — uploaded if present, else default.
const productImageSrc = (p) => `${ASSETS}${p.image || defaultImageFor(p)}`;

// Margin % (selling vs cost) — null when cost is 0 so we don't show ∞%.
const marginPct = (cost, price) => {
  const c = Number(cost), s = Number(price);
  if (!c || c <= 0) return null;
  return ((s - c) / c) * 100;
};

const Products = () => {
  const { facility } = useFacility();
  const [tab] = useState('products'); // tab kept for layout — Categories moved to Settings
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('all');
  const [productOpen, setProductOpen] = useState(false);
  const [form, setForm] = useState(emptyProduct);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [labelProduct, setLabelProduct] = useState(null); // { product, copies }
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const previewSvgRef = useRef(null);

  const load = async () => {
    const [p, c] = await Promise.all([axios.get(productsUrl), axios.get(categoriesUrl)]);
    setProducts(p.data?.results || []);
    setCats(c.data?.results || []);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  // Live render of the barcode preview inside the product modal as the user types,
  // scans or generates a value.
  useEffect(() => {
    renderBarcode(previewSvgRef.current, form.barcode, { width: 1.6, height: 50, fontSize: 12 });
  }, [form.barcode, productOpen]);

  const filtered = useMemo(() => {
    let list = products;
    if (filterCat !== 'all') {
      if (filterCat === 'uncategorized') list = list.filter((p) => !p.category_id);
      else list = list.filter((p) => Number(p.category_id) === Number(filterCat));
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((p) =>
        [p.name, p.sku, p.barcode, p.category_name, p.brand].some((v) => String(v || '').toLowerCase().includes(q))
      );
    }
    return list;
  }, [products, search, filterCat]);

  // Reset to page 1 whenever the filter set changes so we don't end up on an empty page.
  useEffect(() => { setPage(1); }, [search, filterCat, pageSize]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const pageStart = (currentPage - 1) * pageSize;
  const pageEnd = Math.min(pageStart + pageSize, filtered.length);
  const pageRows = filtered.slice(pageStart, pageEnd);

  const stats = useMemo(() => {
    const total = products.length;
    const priceOf = (p) => Number(p.effective_price ?? p.selling_price) || 0;
    const totalPrice = products.reduce((s, p) => s + priceOf(p), 0);
    const avgPrice = total > 0 ? totalPrice / total : 0;
    const margins = products
      .filter((p) => Number(p.cost_price) > 0)
      .map((p) => ((priceOf(p) - Number(p.cost_price)) / Number(p.cost_price)) * 100);
    const avgMargin = margins.length > 0 ? margins.reduce((a, b) => a + b, 0) / margins.length : 0;
    const lowMargin = margins.filter((m) => m < 10).length;
    return {
      total,
      categories: cats.length,
      avgPrice,
      totalPrice,
      avgMargin,
      lowMargin,
      uncategorized: products.filter((p) => !p.category_id).length,
    };
  }, [products, cats]);

  // Stats for the currently-filtered view (used in the analytics footer).
  const viewStats = useMemo(() => {
    const total = filtered.length;
    const priceOf = (p) => Number(p.effective_price ?? p.selling_price) || 0;
    const totalPrice = filtered.reduce((s, p) => s + priceOf(p), 0);
    const margins = filtered
      .filter((p) => Number(p.cost_price) > 0)
      .map((p) => ((priceOf(p) - Number(p.cost_price)) / Number(p.cost_price)) * 100);
    const avgMargin = margins.length > 0 ? margins.reduce((a, b) => a + b, 0) / margins.length : 0;
    return { total, totalPrice, avgMargin };
  }, [filtered]);

  const onImage = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true); setError(null);
    try {
      const fd = new FormData(); fd.append('image', file);
      const res = await axios.post(uploadProductImageUrl, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.data?.success) setForm((f) => ({ ...f, image: res.data.path }));
    } catch (err) { setError(err.response?.data?.message || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const clearUpload = () => setForm((f) => ({ ...f, image: null }));

  // Pull a fresh auto-generated SKU from the backend. Pass the category for a
  // category-flavoured prefix; otherwise it defaults to PRD-.
  const fetchNextSku = async (categoryId = '') => {
    try {
      const params = categoryId ? { category_id: categoryId } : {};
      const res = await axios.get(nextSkuUrl, { params });
      if (res.data?.success) return res.data.sku;
    } catch (_) {}
    return '';
  };

  const openCreate = async () => {
    setEditId(null);
    setError(null);
    setForm(emptyProduct);
    setProductOpen(true);
    const sku = await fetchNextSku();
    setForm((f) => ({ ...f, sku }));
  };

  // Effective markup on the selected category = branch override → category
  // default → null. The backend already computes this per caller-branch in
  // /categories, so we just read `effective_markup_percent` here.
  const activeCategoryMarkup = useMemo(() => {
    const cat = cats.find((c) => Number(c.id) === Number(form.category_id));
    if (!cat) return null;
    const pct = Number(cat.effective_markup_percent);
    return Number.isFinite(pct) && pct > 0 ? pct : null;
  }, [cats, form.category_id]);

  // Whether the effective markup comes from a branch override — used to label
  // the hint as "Your branch's markup" vs "Category markup".
  const activeMarkupSource = useMemo(() => {
    const cat = cats.find((c) => Number(c.id) === Number(form.category_id));
    if (!cat) return null;
    if (cat.branch_markup_percent != null) return 'branch';
    if (cat.default_markup_percent != null) return 'category';
    return null;
  }, [cats, form.category_id]);

  // Derive the suggested selling price from the cost and the category markup.
  // Returns null when we don't have enough info to compute one.
  const suggestedSellingPrice = (cost, pct) => {
    const c = Number(cost);
    const p = Number(pct);
    if (!c || c <= 0 || !p || p <= 0) return null;
    return round2(c * (1 + p / 100));
  };

  // When the admin changes the category in the create form, re-roll the SKU
  // so the prefix matches the new category. Skip while editing — existing SKUs
  // stay frozen for data integrity. If the new category has a markup, ALWAYS
  // recompute the selling price from it: category markup is the source of
  // truth. Manual selling price is only used when there is no category markup.
  const onCategoryChange = async (categoryId) => {
    const cat = cats.find((c) => Number(c.id) === Number(categoryId));
    const pct = Number(cat?.effective_markup_percent);
    setForm((f) => {
      const next = { ...f, category_id: categoryId };
      // If the product opted out of markup pricing, leave its selling price alone.
      if (!f.use_manual_price) {
        const sp = suggestedSellingPrice(f.cost_price, pct);
        if (sp != null) next.selling_price = String(sp);
      }
      return next;
    });
    if (editId) return;
    const sku = await fetchNextSku(categoryId);
    if (sku) setForm((f) => ({ ...f, sku }));
  };

  // Cost changed → if the current category has a markup, always recompute.
  const onCostChange = (val) => {
    setForm((f) => {
      const next = { ...f, cost_price: val };
      if (!f.use_manual_price && activeCategoryMarkup != null) {
        const sp = suggestedSellingPrice(val, activeCategoryMarkup);
        if (sp != null) next.selling_price = String(sp);
      }
      return next;
    });
  };

  // Only meaningful when the category has no markup — otherwise the field is
  // read-only in the UI and the backend overrides anything sent anyway.
  const onSellingPriceChange = (val) => {
    setForm((f) => ({ ...f, selling_price: val }));
  };

  const regenerateSku = async () => {
    if (editId) return;
    const sku = await fetchNextSku(form.category_id);
    if (sku) setForm((f) => ({ ...f, sku }));
  };
  const openEdit = (p) => {
    setForm({
      name: p.name, sku: p.sku, barcode: p.barcode || '', category_id: p.category_id || '',
      brand: p.brand || '', unit: p.unit, cost_price: p.cost_price, selling_price: p.selling_price,
      use_manual_price: !!p.use_manual_price,
      tax_percent: p.tax_percent, reorder_level: p.reorder_level, image: p.image, description: p.description || '',
    });
    setEditId(p.id); setProductOpen(true); setError(null);
  };

  // Live price sanity — both values present, both positive, selling below cost.
  const priceInvalid = (() => {
    const cost = Number(form.cost_price);
    const sell = Number(form.selling_price);
    return cost > 0 && sell > 0 && sell < cost;
  })();

  const submit = async () => {
    if (priceInvalid) {
      setError("Selling price can't be below cost price. Fix that before saving.");
      return;
    }
    setError(null); setSaving(true);
    try {
      const payload = { ...form };
      if (payload.category_id === '') payload.category_id = null;
      if (editId) {
        await axios.patch(productByIdUrl(editId), payload);
        toastSuccess('Product updated');
      } else {
        await axios.post(productsUrl, payload);
        toastSuccess('Product created');
      }
      setProductOpen(false); await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed');
    } finally { setSaving(false); }
  };

  const deactivate = async (p) => {
    const ok = await swalDangerConfirm(`Deactivate ${p.name}?`, 'It will be hidden from the POS and inventory until you reactivate it.', { confirmButtonText: 'Yes, deactivate' });
    if (!ok) return;
    try {
      await axios.delete(productByIdUrl(p.id));
      toastSuccess('Product deactivated');
      load();
    } catch (err) { showAxiosError(err); }
  };

  // Category create / delete moved to Settings → Categories & markup.

  // Live values for the modal's preview pane
  const previewProduct = {
    name: form.name,
    unit: form.unit,
    image: form.image,
    category_name: cats.find((c) => Number(c.id) === Number(form.category_id))?.name || '',
    description: form.description,
  };
  const previewSrc = `${ASSETS}${form.image || defaultImageFor(previewProduct)}`;
  const usingDefault = !form.image;

  return (
    <>
      {/* STATS STRIP — five-card analytics row */}
      <div className="prod-stats">
        <div className="prod-stat tone-brand">
          <div className="ic">📦</div>
          <div className="body">
            <div className="lbl">Active products</div>
            <div className="val">{stats.total}</div>
            <div className="hint">{stats.categories} categories</div>
          </div>
        </div>
        <div className="prod-stat tone-accent">
          <div className="ic">💰</div>
          <div className="body">
            <div className="lbl">Avg selling price</div>
            <div className="val">{fmtMoney(stats.avgPrice)}</div>
            <div className="hint">across the catalog</div>
          </div>
        </div>
        <div className={`prod-stat ${stats.avgMargin >= 30 ? 'tone-success' : stats.avgMargin >= 10 ? 'tone-brand' : 'tone-warn'}`}>
          <div className="ic">📈</div>
          <div className="body">
            <div className="lbl">Avg margin</div>
            <div className="val">{stats.avgMargin.toFixed(0)}%</div>
            <div className="hint">{stats.avgMargin >= 30 ? 'Healthy' : stats.avgMargin >= 10 ? 'Acceptable' : 'Review pricing'}</div>
          </div>
        </div>
        <div className={`prod-stat ${stats.lowMargin > 0 ? 'tone-warn' : 'tone-mute'}`}>
          <div className="ic">⚠️</div>
          <div className="body">
            <div className="lbl">Low-margin items</div>
            <div className="val">{stats.lowMargin}</div>
            <div className="hint">{stats.lowMargin > 0 ? 'below 10% margin' : 'all priced well'}</div>
          </div>
        </div>
        <div className={`prod-stat ${stats.uncategorized > 0 ? 'tone-warn' : 'tone-mute'}`}>
          <div className="ic">🗂️</div>
          <div className="body">
            <div className="lbl">Uncategorized</div>
            <div className="val">{stats.uncategorized}</div>
            <div className="hint">{stats.uncategorized > 0 ? 'needs tagging' : 'all categorized'}</div>
          </div>
        </div>
      </div>

      <div className="panel">
        <div className="panel-head">
          <div>
            <h2>Products</h2>
            <p className="muted-sm" style={{ margin: '2px 0 0' }}>
              {tab === 'products'
                ? 'Manage what your shops sell. Products without an image use a smart default based on category.'
                : 'Organise products into categories for easier browsing in the POS.'}
            </p>
          </div>
          <div className="panel-toolbar">
            {tab === 'products' && (
              <>
                <div className="prod-search">
                  <span className="ic">🔍</span>
                  <input
                    className="f-input"
                    placeholder="Search name, SKU, brand…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <select className="f-select" value={filterCat} onChange={(e) => setFilterCat(e.target.value)} style={{ width: 200 }}>
                  <option value="all">All categories</option>
                  <option value="uncategorized">— Uncategorized —</option>
                  {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <button className="btn-primary" onClick={openCreate}>+ New product</button>
              </>
            )}
          </div>
        </div>

        {tab === 'products' && (
          <>
            {/* Pagination bar — top */}
            {filtered.length > 0 && (
              <div className="prod-pager">
                <div className="pager-info">
                  Showing <strong>{pageStart + 1}–{pageEnd}</strong> of <strong>{filtered.length}</strong>
                  {filtered.length !== products.length && <span className="muted"> (filtered from {products.length})</span>}
                </div>
                <div className="pager-controls">
                  <select className="f-select pager-size" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                    {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n} per page</option>)}
                  </select>
                  <div className="pager-nav">
                    <button className="pager-btn" onClick={() => setPage(1)} disabled={currentPage === 1} title="First page">«</button>
                    <button className="pager-btn" onClick={() => setPage(currentPage - 1)} disabled={currentPage === 1} title="Previous page">‹</button>
                    <span className="pager-page">{currentPage} / {pageCount}</span>
                    <button className="pager-btn" onClick={() => setPage(currentPage + 1)} disabled={currentPage === pageCount} title="Next page">›</button>
                    <button className="pager-btn" onClick={() => setPage(pageCount)} disabled={currentPage === pageCount} title="Last page">»</button>
                  </div>
                </div>
              </div>
            )}

            <div className="tbl-wrap">
              <table className="tbl prod-tbl compact">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Category</th>
                    <th>Unit</th>
                    <th className="right">Cost</th>
                    <th className="right">Price</th>
                    <th className="right">Margin</th>
                    <th className="right">Reorder</th>
                    <th className="right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((p) => {
                    const livePrice = Number(p.effective_price ?? p.selling_price) || 0;
                    const margin = marginPct(p.cost_price, livePrice);
                    return (
                      <tr key={p.id}>
                        <td>
                          <div className="prod-cell">
                            <img
                              src={productImageSrc(p)}
                              alt=""
                              className="prod-thumb"
                              onError={(e) => { e.target.src = `${ASSETS}/uploads/products/defaults/generic.svg`; }}
                            />
                            <div className="prod-name-block">
                              <div className="nm">{p.name}</div>
                              <div className="prod-sub">
                                <code className="prod-sku">{p.sku}</code>
                                {p.brand && <span className="prod-brand-inline">· {p.brand}</span>}
                                {!p.image && <span className="meta-tag">default img</span>}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          {p.category_name
                            ? <span className="prod-cat-pill">{p.category_name}</span>
                            : <span style={{ color: 'var(--mute-2)', fontSize: 12 }}>—</span>}
                        </td>
                        <td><code className="prod-unit">{p.unit}</code></td>
                        <td className="right num">{fmtMoney(p.cost_price)}</td>
                        <td className="right num"><strong>{fmtMoney(livePrice)}</strong></td>
                        <td className="right num">
                          {margin === null ? <span style={{ color: 'var(--mute-2)' }}>—</span> :
                            <span className={`prod-margin ${margin >= 30 ? 'good' : margin >= 10 ? 'ok' : 'low'}`}>
                              {margin.toFixed(0)}%
                            </span>}
                        </td>
                        <td className="right num">{fmtNumber(p.reorder_level)}</td>
                        <td className="right">
                          <div className="row-icons">
                            <button
                              className="icon-btn"
                              onClick={() => setLabelProduct({ product: p, copies: 1 })}
                              title="Print barcode label"
                              aria-label="Print label"
                            >
                              <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M3 7v6a2 2 0 0 0 .59 1.41l5 5a2 2 0 0 0 2.83 0L17 13.83a2 2 0 0 0 0-2.83l-5-5A2 2 0 0 0 10.59 5H4a1 1 0 0 0-1 1z"/>
                                <circle cx="7" cy="9" r="1" fill="currentColor"/>
                              </svg>
                            </button>
                            <button
                              className="icon-btn brand"
                              onClick={() => openEdit(p)}
                              title="Edit product"
                              aria-label="Edit"
                            >
                              <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M13.5 3.5a2.121 2.121 0 1 1 3 3L7 16l-4 1 1-4 9.5-9.5z"/>
                              </svg>
                            </button>
                            <button
                              className="icon-btn danger"
                              onClick={() => deactivate(p)}
                              title="Deactivate product"
                              aria-label="Remove"
                            >
                              <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M4 6h12M8 6V4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2M6 6l1 10a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1l1-10"/>
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {filtered.length === 0 && (
                    <tr><td colSpan="8" className="prod-empty-cell">
                      {search || filterCat !== 'all'
                        ? 'No products match the current filters.'
                        : 'No products yet. Add your first one.'}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Analytics footer — shows totals for the filtered view */}
            {filtered.length > 0 && (
              <div className="prod-analytics">
                <div className="analytics-card">
                  <div className="lbl">Items shown</div>
                  <div className="val">{filtered.length}</div>
                </div>
                <div className="analytics-card">
                  <div className="lbl">Catalog value</div>
                  <div className="val">{fmtMoney(viewStats.totalPrice)}</div>
                  <div className="sub">sum of selling prices</div>
                </div>
                <div className="analytics-card">
                  <div className="lbl">Avg margin</div>
                  <div className={`val ${viewStats.avgMargin >= 30 ? 'success' : viewStats.avgMargin >= 10 ? 'brand' : 'warn'}`}>
                    {viewStats.avgMargin.toFixed(0)}%
                  </div>
                </div>
                <div className="analytics-card">
                  <div className="lbl">Page</div>
                  <div className="val">{currentPage} of {pageCount}</div>
                </div>
              </div>
            )}
          </>
        )}

      </div>

      <Modal
        title={editId ? `Edit product — ${form.name || ''}` : 'New product'}
        open={productOpen}
        onClose={() => setProductOpen(false)}
        width={1040}
        footer={
          <>
            <div style={{ color: 'var(--mute)', fontSize: 13, marginRight: 'auto' }}>
              {usingDefault
                ? <>📷 Will use the <strong style={{ color: 'var(--brand)' }}>default {form.unit || 'product'} image</strong></>
                : <>📷 Custom image uploaded</>}
            </div>
            <button className="btn-ghost" onClick={() => setProductOpen(false)}>Cancel</button>
            <button className="btn-primary" onClick={submit} disabled={saving || !form.name || !form.sku || priceInvalid}>
              {saving ? 'Saving…' : (editId ? 'Save changes' : 'Create product')}
            </button>
          </>
        }
      >
        {error && <div className="notice">{error}</div>}

        <div className="role-modal-layout">
          {/* LEFT — live preview */}
          <aside className="role-modal-aside prod-preview-aside">
            <div className="role-modal-aside-label">Product preview</div>
            <motion.img
              key={previewSrc}
              src={previewSrc}
              alt={form.name || 'preview'}
              className="prod-preview-img"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.25 }}
            />
            <div className="prod-preview-name">{form.name || 'Unnamed product'}</div>
            {form.brand && <div className="prod-preview-brand">{form.brand}</div>}
            <div className="prod-preview-row">
              <span className="prod-preview-sku">{form.sku || 'SKU?'}</span>
              <code className="prod-unit">{form.unit}</code>
            </div>
            <div className="prod-preview-price">
              <div>
                <div className="lbl">Selling</div>
                <div className="val">{form.selling_price ? fmtMoney(form.selling_price) : '—'}</div>
              </div>
              <div>
                <div className="lbl">Cost</div>
                <div className="val muted">{form.cost_price ? fmtMoney(form.cost_price) : '—'}</div>
              </div>
            </div>

            <div className="prod-image-controls">
              <label className="prod-image-upload">
                <input type="file" accept="image/*" onChange={onImage} disabled={uploading} />
                <span>{uploading ? 'Uploading…' : (form.image ? '📷 Replace image' : '📷 Upload custom image')}</span>
              </label>
              {form.image && (
                <button type="button" className="btn-link" onClick={clearUpload} style={{ color: 'var(--danger)' }}>
                  Use default instead
                </button>
              )}
              {usingDefault && (
                <p className="prod-default-note">
                  💡 No upload? The system picks an image based on the unit (<code>{form.unit}</code>) and category.
                  You can change the unit to swap the default.
                </p>
              )}
            </div>
          </aside>

          {/* RIGHT — form fields */}
          <section>
            <h4 className="prod-section-head">Identity</h4>
            <div className="f-grid f-grid-2">
              <div className="f-row">
                <label className="f-label">Name *</label>
                <input className="f-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Paracetamol 500mg" />
              </div>
              <div className="f-row">
                <label className="f-label">
                  Product ID (SKU) *
                  {editId
                    ? <span style={{ color: 'var(--mute-2)', textTransform: 'none', fontWeight: 500, marginLeft: 6 }}>(locked)</span>
                    : <span className="sku-auto-tag">auto</span>}
                </label>
                <div className="sku-field">
                  <input
                    className="f-input sku-input"
                    value={form.sku}
                    readOnly
                    placeholder="Generating…"
                    disabled={!!editId}
                  />
                  {!editId && (
                    <button
                      type="button"
                      className="sku-regen-btn"
                      onClick={regenerateSku}
                      title="Generate a different SKU"
                    >↻</button>
                  )}
                </div>
                {!editId && (
                  <p style={{ fontSize: 11.5, color: 'var(--mute)', margin: '6px 0 0' }}>
                    Auto-generated from the category. Changes when you pick a different category.
                  </p>
                )}
              </div>
              <div className="f-row">
                <label className="f-label">Brand</label>
                <input className="f-input" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} placeholder="Manufacturer / line" />
              </div>
              <div className="f-row">
                <label className="f-label">Category</label>
                <select className="f-select" value={form.category_id} onChange={(e) => onCategoryChange(e.target.value)}>
                  <option value="">— None —</option>
                  {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="f-row">
                <label className="f-label">Unit / form</label>
                <select className="f-select" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}>
                  {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              <div className="f-row" style={{ gridColumn: '1 / -1' }}>
                <label className="f-label">Barcode</label>
                <div className="barcode-field">
                  <input
                    className="f-input"
                    value={form.barcode}
                    onChange={(e) => setForm({ ...form, barcode: e.target.value })}
                    placeholder="Scan with a USB scanner, type, or generate one"
                  />
                  <button
                    type="button"
                    className="barcode-btn scan"
                    onClick={() => setScannerOpen(true)}
                    title="Open camera scanner"
                  >📷 Scan</button>
                  <button
                    type="button"
                    className="barcode-btn gen"
                    onClick={() => setForm((f) => ({ ...f, barcode: generateEAN13() }))}
                    title="Generate a fresh internal-use EAN-13"
                  >✨ Generate</button>
                </div>
                {form.barcode && (
                  <div className="barcode-preview">
                    <svg ref={previewSvgRef} />
                    <span className="barcode-preview-hint">Preview · prints on the label</span>
                  </div>
                )}
                {!form.barcode && (
                  <p className="prod-default-note" style={{ marginTop: 8 }}>
                    💡 No barcode? Click <strong>Generate</strong> to mint an internal EAN-13 starting with <code>200</code>, then print the label for the shelf.
                  </p>
                )}
              </div>
            </div>

            <h4 className="prod-section-head">Pricing</h4>
            {/* Per-product opt-out — when this is on, the markup chain is ignored
                for this product and the manually-entered selling price is used
                everywhere (POS, inventory, reports). */}
            <div className="f-row" style={{ marginBottom: 10 }}>
              <label className="manual-price-toggle">
                <input
                  type="checkbox"
                  checked={!!form.use_manual_price}
                  onChange={(e) => setForm((f) => ({ ...f, use_manual_price: e.target.checked }))}
                />
                <span>
                  <strong>Use my own selling price for this product</strong>
                  <em>Ignore the branch / category markup and use the price I type below. Useful for regulated items, promotions, or products with a fixed shelf price.</em>
                </span>
              </label>
            </div>
            <div className="f-grid f-grid-2">
              <div className="f-row">
                <label className="f-label">Cost price (GHS) *</label>
                <input
                  className={`f-input ${priceInvalid ? 'has-error' : ''}`}
                  type="number" step="0.01" min="0"
                  value={form.cost_price}
                  onChange={(e) => onCostChange(e.target.value)}
                />
                {!form.use_manual_price && activeCategoryMarkup != null && (
                  <div className="f-hint">
                    {activeMarkupSource === 'branch'
                      ? <>Your branch's markup on this category: <strong>{activeCategoryMarkup}%</strong> — selling price is calculated from cost automatically.</>
                      : <>Category markup: <strong>{activeCategoryMarkup}%</strong> — selling price is calculated from cost automatically.</>}
                  </div>
                )}
                {!form.use_manual_price && form.category_id && activeCategoryMarkup == null && (
                  <div className="f-hint warn">
                    Neither this branch nor the category has a markup set. Add one in <strong>Settings → Categories &amp; markup</strong> to auto-price products.
                  </div>
                )}
              </div>
              <div className="f-row">
                <label className="f-label">
                  Selling price (GHS) *
                  {!form.use_manual_price && activeCategoryMarkup != null && <span className="pill-auto"> Auto</span>}
                  {form.use_manual_price && <span className="pill-manual"> Manual</span>}
                </label>
                <input
                  className={`f-input ${priceInvalid ? 'has-error' : ''} ${!form.use_manual_price && activeCategoryMarkup != null ? 'is-locked' : ''}`}
                  type="number" step="0.01" min="0"
                  value={form.selling_price}
                  onChange={(e) => onSellingPriceChange(e.target.value)}
                  readOnly={!form.use_manual_price && activeCategoryMarkup != null}
                  placeholder={!form.use_manual_price && activeCategoryMarkup != null ? 'Derived from cost × category markup' : 'Enter selling price'}
                />
                {form.use_manual_price ? (
                  <div className="f-hint">
                    <strong>Override on.</strong> This exact price will be used at the POS. Markup changes to the branch or category will not affect this product.
                  </div>
                ) : activeCategoryMarkup != null ? (
                  <div className="f-hint">
                    Locked by the <strong>{activeCategoryMarkup}%</strong> {activeMarkupSource === 'branch' ? 'branch-specific' : 'category default'} markup. Update it in <strong>Settings → Categories &amp; markup</strong> to change the price — or tick the checkbox above to enter a manual price for this product only.
                  </div>
                ) : (
                  <div className="f-hint">
                    No markup configured — enter the selling price manually.
                  </div>
                )}
              </div>
              {priceInvalid && (
                <div className="f-row" style={{ gridColumn: '1 / -1', marginBottom: 0 }}>
                  <div className="price-error">
                    <span className="ic">⚠️</span>
                    <div>
                      <strong>Selling price can't be below cost price.</strong>
                      <p>You'd lose <strong>{fmtMoney(Number(form.cost_price) - Number(form.selling_price))}</strong> on every sale. Raise the selling price or lower the cost.</p>
                    </div>
                  </div>
                </div>
              )}
              <div className="f-row">
                <label className="f-label">Tax %</label>
                <input className="f-input" type="number" step="0.01" value={form.tax_percent} onChange={(e) => setForm({ ...form, tax_percent: e.target.value })} />
              </div>
              <div className="f-row">
                <label className="f-label">Reorder level</label>
                <input className="f-input" type="number" value={form.reorder_level} onChange={(e) => setForm({ ...form, reorder_level: e.target.value })} placeholder="Alert when stock drops below this" />
              </div>
              {!editId && (
                <div className="f-row" style={{ gridColumn: '1 / -1' }}>
                  <label className="f-label">Opening stock (this branch)</label>
                  <input className="f-input" type="number" step="0.001" value={form.initial_stock} onChange={(e) => setForm({ ...form, initial_stock: e.target.value })} placeholder="0" />
                </div>
              )}
            </div>

            <h4 className="prod-section-head">Notes</h4>
            <div className="f-row">
              <label className="f-label">Description</label>
              <textarea
                className="f-textarea"
                rows={3}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Optional — what it's for, dosage, ingredients, etc."
                style={{ resize: 'vertical' }}
              />
            </div>
          </section>
        </div>
      </Modal>

      {/* Camera-based barcode scanner */}
      <BarcodeScannerModal
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onResult={(code) => setForm((f) => ({ ...f, barcode: code }))}
      />

      {/* Printable label */}
      <Modal
        title={labelProduct ? `Print label — ${labelProduct.product.name}` : 'Print label'}
        open={!!labelProduct} onClose={() => setLabelProduct(null)} width={520}
        footer={
          <>
            <button className="btn-ghost" onClick={() => setLabelProduct(null)}>Close</button>
            <button className="btn-primary" onClick={() => window.print()}>🖨️ Print</button>
          </>
        }
      >
        {labelProduct && (
          <>
            <div className="label-controls">
              <label className="f-label">Copies</label>
              <div style={{ display: 'flex', gap: 6 }}>
                {[1, 2, 4, 8, 12].map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={`label-copy-btn ${labelProduct.copies === n ? 'active' : ''}`}
                    onClick={() => setLabelProduct({ ...labelProduct, copies: n })}
                  >× {n}</button>
                ))}
              </div>
            </div>
            <BarcodeLabel
              product={labelProduct.product}
              facility={facility}
              copies={labelProduct.copies}
            />
            <p style={{ color: 'var(--mute)', fontSize: 12, marginTop: 14, textAlign: 'center' }}>
              The label uses the product's barcode if present, otherwise the SKU. Hit <strong>Print</strong> and select your label printer or printable paper.
            </p>
          </>
        )}
      </Modal>
    </>
  );
};

export default Products;
